import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, Messaging } from "firebase/messaging";
import { getFirestore, doc, setDoc, arrayUnion, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { store } from "../store/store";
import { setUser } from "../features/auth/authSlice";
import { API_BASE_URL } from "../api/backendConfig";

const readEnv = (key: string): string => {
  const value = process.env[key];
  return typeof value === "string" ? value.trim() : "";
};

const firebaseConfig = {
  apiKey: "AIzaSyAoXj_E1m45filLrurs_zzo3zUR_QC9wTQ",
  authDomain: "mycal-d3c9c.firebaseapp.com",
  projectId: "mycal-d3c9c",
  storageBucket: "mycal-d3c9c.firebasestorage.app",
  messagingSenderId: "29853640588",
  appId: "1:29853640588:web:15863410425c3e70b898a9",
  measurementId: "G-H46KTTKNZ4"
};

const app = initializeApp(firebaseConfig);
console.log("[Firebase] Initialized project:", firebaseConfig.projectId);
export const db = getFirestore(app);
export const auth = getAuth(app);
const normalizeEmail = (email?: string): string => (email || "").trim().toLowerCase();

let messagingInstance: unknown = null;
let messagingInitPromise: Promise<unknown> | null = null;

export const getMessagingInstance = async (): Promise<unknown> => {
  if (messagingInstance) return messagingInstance;

  if (!messagingInitPromise) {
    messagingInitPromise = isSupported()
      .then((supported) => {
        if (!supported) {
          console.warn("[Firebase] Messaging is not supported in this browser");
          return null;
        }
        return getMessaging(app);
      })
      .catch((error) => {
        console.error("[Firebase] Failed to initialize messaging", error);
        return null;
      })
      .then((instance) => {
        messagingInstance = instance;
        return instance;
      });
  }

  return messagingInitPromise;
};

const saveFCMToken = async (token: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log("User not authenticated, skipping token save");
      return;
    }

    const userDocRef = doc(db, "RegistrationTokens", user.email!);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const existingTokens = userDocSnap.data().tokens || [];
      if (!existingTokens.includes(token)) {
        await setDoc(userDocRef, {
          tokens: arrayUnion(token),
          lastUpdated: new Date(),
          user: {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL
          }
        }, { merge: true });
      }
    } else {
      await setDoc(userDocRef, {
        tokens: [token],
        lastUpdated: new Date(),
        user: {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL
          }
      });
      console.log("New document created with token");
    }
  } catch (error) {
    console.error("Error saving token to Firestore:", error);
  }
};

let currentFCMToken: string | null = null;
let authInitialized = false;
let hasHadUser = false;
let authStateUnsubscribe: (() => void) | null = null;

export const initializeAuthStateListener = (): void => {
  if (authStateUnsubscribe) return;
  
  authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!authInitialized) {
      authInitialized = true;
      console.log('[Firebase] Auth state initialized, user:', user ? user.email : 'null');
    }

    if (user) {
      hasHadUser = true;

      const userData = {
        email: user.email,
        name: user.displayName,
        picture: user.photoURL,
        sub: user.uid
      };
      try {
        sessionStorage.setItem("user", JSON.stringify(userData));
        console.log('[Firebase] Synced user to sessionStorage:', user.email);

        store.dispatch(setUser(userData));
        console.log('[Firebase] Updated Redux auth state');
      } catch (e) {
        console.error('[Firebase] Error setting sessionStorage:', e);
      }

      if (currentFCMToken) {
        await saveFCMToken(currentFCMToken);
      }
    } else {
      if (authInitialized && hasHadUser) {
        console.log("Firebase Auth user signed out");
        try {
          sessionStorage.removeItem("user");
          store.dispatch(setUser(null));
          console.log('[Firebase] Cleared Redux auth state');
        } catch (e) {
          console.error('[Firebase] Error clearing sessionStorage:', e);
        }
        currentFCMToken = null;
      } else if (authInitialized && !hasHadUser) {
        try {
          const existingUser = sessionStorage.getItem("user");
          if (existingUser) {
            console.log('[Firebase] No Firebase user but sessionStorage has user data - keeping it');
            const parsedUser = JSON.parse(existingUser);
            store.dispatch(setUser(parsedUser));
            console.log('[Firebase] Updated Redux with existing sessionStorage user');
          } else {
            store.dispatch(setUser(null));
          }
        } catch (e) {
          store.dispatch(setUser(null));
        }
      }
    }
  });
};

export const signOutUser = async (): Promise<void> => {
  try {
    try {
      await fetch(`${API_BASE_URL}/user/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn("[Firebase] FastAPI session logout failed:", e);
    }
    await signOut(auth);
    sessionStorage.removeItem("user");
    store.dispatch(setUser(null));
    window.location.reload();
  } catch (error) {
    console.error("Error signing out:", error);
  }
};

export async function requestPermission(serviceWorkerRegistration: ServiceWorkerRegistration | null): Promise<string | null> {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    if (typeof Notification === "undefined") {
      console.warn("[Firebase] Notifications API not available in this environment");
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("[Firebase] Notification permission not granted:", permission);
      return null;
    }

    const token = await getToken(messaging as Messaging, {
      vapidKey:
        "BNjxiwFACBpJEh5NK9RHlb57QlO9WbTL2vCP1lbS_eSLS11W4Bwulid19cEUI39aVZVkgyLY-pT_eR7HrBfMxpA",
      serviceWorkerRegistration,
    }).catch((err) => {
      console.error("[Firebase] Error getting FCM token:", err);
      return null;
    });

    if (!token) return null;

    currentFCMToken = token;

    if (auth.currentUser) {
      await saveFCMToken(token);
    }

    return token;
  } catch (error) {
    console.error("[Firebase] Failed to request notification permission:", error);
    return null;
  }
}

type NotificationDataRecord = Record<string, unknown>;

const parseRowsWithMissingFields = (
  data: NotificationDataRecord,
): Array<Record<string, unknown>> => {
  const rowsJson = data.rows_w_missing_fields_json;
  if (typeof rowsJson === "string") {
    try {
      const parsed = JSON.parse(rowsJson);
      if (Array.isArray(parsed)) {
        return parsed.filter((row) => !!row && typeof row === "object") as Array<
          Record<string, unknown>
        >;
      }
    } catch {
      // ignore and fallback below
    }
  }
  const directRows = data.rows_w_missing_fields;
  if (Array.isArray(directRows)) {
    return directRows.filter((row) => !!row && typeof row === "object") as Array<
      Record<string, unknown>
    >;
  }
  return [];
};

const formatMissingFieldsReport = (
  rows: Array<Record<string, unknown>>,
): string => {
  if (!rows.length) {
    return "Contacts with missing required fields were reported.";
  }
  return rows
    .map((row, index) => {
      const label =
        typeof row.contact === "string" && row.contact.trim()
          ? row.contact
          : typeof row.id === "string" && row.id.trim()
            ? row.id
            : `Row ${String(row.row_index ?? index + 1)}`;
      const missing = Array.isArray(row.missing_fields)
        ? row.missing_fields.map((x) => String(x).trim()).filter(Boolean).join(", ")
        : "";
      return missing ? `${label}: ${missing}` : `${label}: missing required fields`;
    })
    .join("\n");
};

const parseJsonArrayField = (
  data: NotificationDataRecord,
  jsonKey: string,
  directKey: string,
): Array<Record<string, unknown>> => {
  const jsonValue = data[jsonKey];
  if (typeof jsonValue === "string") {
    try {
      const parsed = JSON.parse(jsonValue);
      if (Array.isArray(parsed)) {
        return parsed.filter((row) => !!row && typeof row === "object") as Array<
          Record<string, unknown>
        >;
      }
    } catch {
      // fall through
    }
  }
  const directValue = data[directKey];
  if (Array.isArray(directValue)) {
    return directValue.filter((row) => !!row && typeof row === "object") as Array<
      Record<string, unknown>
    >;
  }
  return [];
};

const formatProposedMessagesReport = (
  proposedMessages: Array<Record<string, unknown>>,
): string => {
  if (!proposedMessages.length) {
    return (
      "A crafted outreach message is ready for your approval. " +
      "Reply **approve** to schedule or send feedback to revise."
    );
  }
  const blocks = proposedMessages.map((entry, index) => {
    const label =
      typeof entry.contact === "string" && entry.contact.trim()
        ? entry.contact.trim()
        : typeof entry.contact_name === "string" && entry.contact_name.trim()
          ? entry.contact_name.trim()
          : typeof entry.contact_id === "string" && entry.contact_id.trim()
            ? entry.contact_id.trim()
            : `Contact ${index + 1}`;
    const draft =
      typeof entry.message === "string" && entry.message.trim()
        ? entry.message.trim()
        : typeof entry.text === "string" && entry.text.trim()
          ? entry.text.trim()
          : "_No draft text_";
    const sendAt =
      typeof entry.proposed_send_at_local === "string" && entry.proposed_send_at_local.trim()
        ? entry.proposed_send_at_local.trim()
        : typeof entry.proposed_send_at_iso === "string" && entry.proposed_send_at_iso.trim()
          ? entry.proposed_send_at_iso.trim()
          : null;
    const sendLine = sendAt ? `Proposed send time: ${sendAt}\n\n` : "";
    return `### ${label}\n${sendLine}${draft}`;
  });
  return (
    `${blocks.join("\n\n---\n\n")}\n\n` +
    "Reply **approve** to schedule these messages, or send feedback to request changes."
  );
};

const ensureContactsMissingFieldsSession = async (
  sessionDocRef: ReturnType<typeof doc>,
  sessionExists: boolean,
  normalizedThreadId: string,
  normalizedUserEmail: string,
  payload: NotificationDataRecord,
  type: string,
): Promise<void> => {
  const rows = parseRowsWithMissingFields(payload);
  const report = formatMissingFieldsReport(rows);

  if (sessionExists) {
    await setDoc(
      sessionDocRef,
      {
        rows_w_missing_fields: rows,
        notification_type: type,
        last_updated: new Date().toISOString(),
      },
      { merge: true },
    );
    return;
  }

  await setDoc(
    sessionDocRef,
    {
      thread_id: normalizedThreadId,
      user: normalizedUserEmail,
      title: "Contacts Missing Required Fields",
      agent_type: "CustomerProfileAgent",
      notification_type: type,
      rows_w_missing_fields: rows,
      date_created: new Date().toISOString(),
      last_updated: new Date().toISOString(),
      messages: [
        {
          role: "ai",
          type: "ai",
          title: "Missing Fields Report",
          content: report,
          thread_id: normalizedThreadId,
          rows_w_missing_fields: rows,
          file: null,
        },
      ],
    },
    { merge: true },
  );
};

const ensurePlanMessageApprovalSession = async (
  sessionDocRef: ReturnType<typeof doc>,
  sessionExists: boolean,
  normalizedThreadId: string,
  normalizedUserEmail: string,
  payload: NotificationDataRecord,
  type: string,
): Promise<void> => {
  const proposedMessages = parseJsonArrayField(
    payload,
    "proposed_messages_json",
    "proposed_messages",
  );
  const filteredContacts = parseJsonArrayField(payload, "contacts_json", "filtered_contacts");
  const parentThreadId =
    typeof payload.parent_thread_id === "string" ? payload.parent_thread_id.trim() : "";
  const report = formatProposedMessagesReport(proposedMessages);
  const titleContact =
    proposedMessages.length === 1
      ? String(
          proposedMessages[0]?.contact ||
            proposedMessages[0]?.contact_name ||
            proposedMessages[0]?.contact_id ||
            "Draft Ready",
        ).trim()
      : `${proposedMessages.length || 1} contact(s)`;

  const sessionPatch: Record<string, unknown> = {
    thread_id: normalizedThreadId,
    user: normalizedUserEmail,
    title: `Plan Message: ${titleContact}`,
    agent_type: "PlanMessageAgent",
    notification_type: type,
    proposed_messages: proposedMessages,
    filtered_contacts: filteredContacts,
    awaiting_approval: true,
    unhandled: true,
    last_updated: new Date().toISOString(),
  };
  if (parentThreadId) {
    sessionPatch.parent_thread_id = parentThreadId;
  }

  if (sessionExists) {
    await setDoc(sessionDocRef, sessionPatch, { merge: true });
    return;
  }

  await setDoc(
    sessionDocRef,
    {
      ...sessionPatch,
      date_created: new Date().toISOString(),
      messages: [
        {
          role: "ai",
          type: "ai",
          title: "Message Draft for Approval",
          content: report,
          thread_id: normalizedThreadId,
          proposed_messages: proposedMessages,
          filtered_contacts: filteredContacts,
          file: null,
        },
      ],
    },
    { merge: true },
  );
};

export const ensureNotificationSession = async ({
  userEmail,
  threadId,
  notificationData,
}: {
  userEmail?: string | null;
  threadId?: string | null;
  notificationData?: NotificationDataRecord;
}): Promise<void> => {
  const normalizedUserEmail = normalizeEmail(userEmail || "");
  const normalizedThreadId = (threadId || "").trim();
  if (!normalizedUserEmail || !normalizedThreadId) return;

  const payload = notificationData || {};
  const type = String(payload.type || "");
  if (
    type !== "contacts_missing_required_fields" &&
    type !== "plan_message_schedule_approval"
  ) {
    return;
  }

  const sessionDocRef = doc(
    db,
    "sessionsList",
    normalizedUserEmail,
    "sessions",
    normalizedThreadId,
  );
  const sessionDoc = await getDoc(sessionDocRef);
  const sessionExists = sessionDoc.exists();

  if (type === "contacts_missing_required_fields") {
    await ensureContactsMissingFieldsSession(
      sessionDocRef,
      sessionExists,
      normalizedThreadId,
      normalizedUserEmail,
      payload,
      type,
    );
    return;
  }

  await ensurePlanMessageApprovalSession(
    sessionDocRef,
    sessionExists,
    normalizedThreadId,
    normalizedUserEmail,
    payload,
    type,
  );
};
